/**
 * useViewport.ts
 * Hook standardisé pour gérer le viewport du Stage React-Konva
 *
 * Fonctionnalités:
 * 1. Stage width/height dépend du container (ResizeObserver)
 * 2. Viewport dans le store (scale + position)
 * 3. Zoom centré sur pointer
 * 4. Pan avec espace
 */
import { useRef, useCallback, useEffect, useState } from 'react';
import Konva from 'konva';
import { spaceKeyManager } from '../../../services/SpaceKeyManager';
import { useAppStore } from '../../../services/StateService';

interface UseViewportProps {
  stageRef: React.RefObject<Konva.Stage | null>;
  containerRef: React.RefObject<HTMLDivElement | null>;
  templateWidth: number;
  templateHeight: number;
}

/**
 * Hook standardisé pour gérer le viewport du Stage
 */
export function useViewport({
  stageRef,
  containerRef,
  templateWidth: _templateWidth,
  templateHeight: _templateHeight,
}: UseViewportProps) {
  // Viewport depuis Zustand (source unique de vérité)
  const viewport = useAppStore(state => state.canvas.viewport);
  const setViewport = useAppStore(state => state.setViewport);
  const _setViewportScale = useAppStore(state => state.setViewportScale);
  const setViewportPosition = useAppStore(state => state.setViewportPosition);
  const setZoomLevel = useAppStore(state => state.setZoomLevel);

  // État local pour le pan
  const isPanningRef = useRef(false);
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef<{ x: number; y: number } | null>(null);
  const panStartStagePosRef = useRef<{ x: number; y: number } | null>(null);
  const isSpacePressedRef = useRef(false);

  // Dimensions du container (mises à jour par ResizeObserver)
  const [containerSize, setContainerSize] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });

  // 1. ResizeObserver pour adapter Stage width/height au container
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateContainerSize = () => {
      const rect = container.getBoundingClientRect();
      const newWidth = rect.width > 0 ? rect.width : window.innerWidth;
      const newHeight = rect.height > 0 ? rect.height : window.innerHeight;

      setContainerSize({
        width: newWidth,
        height: newHeight,
      });
    };

    // Mise à jour initiale
    updateContainerSize();

    const resizeObserver = new ResizeObserver(() => {
      updateContainerSize();
    });

    resizeObserver.observe(container);
    window.addEventListener('resize', updateContainerSize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateContainerSize);
    };
  }, [containerRef]);

  // Mettre à jour Stage width/height quand containerSize change
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || containerSize.width === 0 || containerSize.height === 0) return;

    // Mettre à jour les dimensions du Stage
    stage.width(containerSize.width);
    stage.height(containerSize.height);
    stage.batchDraw();
  }, [stageRef, containerSize]);

  // 2. Synchroniser SpaceKeyManager
  useEffect(() => {
    const unsubscribe = spaceKeyManager.subscribe((isDown: boolean) => {
      isSpacePressedRef.current = isDown;
      const stage = stageRef.current;
      if (stage) {
        if (isDown && !isPanningRef.current) {
          stage.container().style.cursor = 'grab';
        } else if (!isDown && !isPanningRef.current) {
          stage.container().style.cursor = 'default';
        }
      }
    });

    return unsubscribe;
  }, [stageRef]);

  // 3. Synchroniser stage.scale() et stage.position() avec le store
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;

    const currentScale = stage.scaleX();
    const currentPos = stage.position();

    // Synchroniser scale
    if (Math.abs(currentScale - viewport.scale) > 0.001) {
      stage.scale({ x: viewport.scale, y: viewport.scale });
    }

    // Synchroniser position
    if (
      Math.abs(currentPos.x - viewport.position.x) > 0.5 ||
      Math.abs(currentPos.y - viewport.position.y) > 0.5
    ) {
      stage.position(viewport.position);
    }

    stage.batchDraw();
  }, [stageRef, viewport.scale, viewport.position]);

  // 4. Zoom centré sur pointer
  const handleWheel = useCallback(
    (e: Konva.KonvaEventObject<WheelEvent>) => {
      e.evt.preventDefault();

      const stage = stageRef.current;
      if (!stage) return;

      const pointer = stage.getPointerPosition();
      if (!pointer) return;

      const oldScale = viewport.scale;
      const oldPos = viewport.position;

      // Calculer le point relatif dans le contenu (avant zoom)
      const mousePointTo = {
        x: (pointer.x - oldPos.x) / oldScale,
        y: (pointer.y - oldPos.y) / oldScale,
      };

      // Calculer le nouveau scale
      const direction = e.evt.deltaY > 0 ? -1 : 1;
      const scaleBy = 1.1;
      const newScale = direction > 0 ? oldScale * scaleBy : oldScale / scaleBy;
      const clampedScale = Math.max(0.1, Math.min(5, newScale));

      // Calculer la nouvelle position pour garder le pointer stable
      const newPos = {
        x: pointer.x - mousePointTo.x * clampedScale,
        y: pointer.y - mousePointTo.y * clampedScale,
      };

      // Mettre à jour le stage
      stage.scale({ x: clampedScale, y: clampedScale });
      stage.position(newPos);
      stage.batchDraw();

      // Mettre à jour le store (source unique de vérité)
      setViewport({ scale: clampedScale, position: newPos });
      setZoomLevel(clampedScale);
    },
    [stageRef, viewport.scale, viewport.position, setViewport, setZoomLevel]
  );

  // 5. Pan avec espace (ou clic droit/milieu/touch)
  const handlePanStart = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
      const stage = stageRef.current;
      if (!stage) return false;

      const isRightClick = e.evt instanceof MouseEvent && e.evt.button === 2;
      const isMiddleClick = e.evt instanceof MouseEvent && e.evt.button === 1;
      const isLeftClickWithSpace =
        e.evt instanceof MouseEvent && e.evt.button === 0 && isSpacePressedRef.current;
      const isTouch = e.evt instanceof TouchEvent;

      // Vérifier si on clique sur le stage ou le background
      interface KonvaNodeWithName extends Konva.Node {
        getName?: () => string;
      }
      const targetName = (e.target as KonvaNodeWithName).getName?.();
      const targetParent = e.target.getParent();
      const _parentName = targetParent ? (targetParent as KonvaNodeWithName).getName?.() : null;

      const isStageOrBackground =
        e.target === stage ||
        targetName === 'background-rect' ||
        (targetParent &&
          (targetParent.getClassName() === 'Layer' || targetParent.getClassName() === 'Group') &&
          targetName === undefined &&
          !(e.target as KonvaNodeWithName).id());

      if (
        (isRightClick || isMiddleClick || isLeftClickWithSpace || isTouch) &&
        isStageOrBackground
      ) {
        e.evt.preventDefault();
        e.evt.stopPropagation();
        isPanningRef.current = true;
        setIsPanning(true);
        const pointer = stage.getPointerPosition();
        if (pointer) {
          panStartRef.current = pointer;
          panStartStagePosRef.current = { ...viewport.position };
          stage.container().style.cursor = 'grabbing';
        }
        return true;
      }
      return false;
    },
    [stageRef, viewport.position]
  );

  const handlePanMove = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
      if (!isPanningRef.current || !panStartRef.current || !panStartStagePosRef.current) return;

      const stage = stageRef.current;
      if (!stage) return;

      e.evt.preventDefault();
      const pointer = stage.getPointerPosition();
      if (pointer) {
        const dx = pointer.x - panStartRef.current.x;
        const dy = pointer.y - panStartRef.current.y;
        const newPos = {
          x: panStartStagePosRef.current.x + dx,
          y: panStartStagePosRef.current.y + dy,
        };

        // Mettre à jour le stage
        stage.position(newPos);
        stage.batchDraw();

        // Mettre à jour le store (source unique de vérité)
        setViewportPosition(newPos);
      }
    },
    [setViewportPosition]
  );

  const handlePanEnd = useCallback(() => {
    if (isPanningRef.current) {
      isPanningRef.current = false;
      setIsPanning(false);
      panStartRef.current = null;
      panStartStagePosRef.current = null;
      const stage = stageRef.current;
      if (stage) {
        stage.container().style.cursor = isSpacePressedRef.current ? 'grab' : 'default';
      }
    }
  }, [stageRef]);

  return {
    // Dimensions du container
    containerSize,
    // État du pan
    isPanning,
    // Handlers
    handleWheel,
    handlePanStart,
    handlePanMove,
    handlePanEnd,
  };
}
